import type {Language} from '../../types/types.ts';

// Re-export for backward compatibility
export type {Language};

const UI_STRINGS: Record<Language, Record<string, string>> = {
	en: {
		nextPage: 'Next page',
		nextPageHint: 'Click on {button} or use key {key} to start reading.',
		swipeRight: 'Swipe right to see the table of contents.',
		swipeUp: 'Swipe up to go to the next article.',
		goBack: 'Go back',
		goForward: 'Go forward',
		tableOfContents: 'Table of contents',
		nextArticle: 'Next article',
		hideDone: 'Hide done',
		showDone: 'Show done',
		articlesMarkedAsRead: 'Articles are marked as read when you navigate past the last page',
		orUseShortcut: ', or use shortcut',
		todaysEdition: "Today's edition summarized",
		loadingSummary: 'Loading summary...',
		summaryUnavailable: 'Summary temporarily unavailable',
		summaryDisclaimer: 'This summary was generated by AI and is awaiting editorial review.',
		summaryApproved: 'This AI-generated summary has been editorially reviewed.'
	},
	no: {
		nextPage: 'Neste side',
		nextPageHint: 'Klikk på {button} eller bruk tast {key} for å begynne å lese.',
		swipeRight: 'Sveip til høyre for å se innholdsfortegnelsen.',
		swipeUp: 'Sveip opp for å gå til neste artikkel.',
		goBack: 'Gå tilbake',
		goForward: 'Gå videre',
		tableOfContents: 'Innholdsfortegnelse',
		nextArticle: 'Neste artikkel',
		hideDone: 'Skjul leste',
		showDone: 'Vis leste',
		articlesMarkedAsRead: 'Artikler markeres som lest når du navigerer forbi siste side',
		orUseShortcut: ', eller bruk snarvei',
		todaysEdition: 'Dagens utgave oppsummert',
		loadingSummary: 'Laster sammendrag...',
		summaryUnavailable: 'Sammendrag midlertidig utilgjengelig',
		summaryDisclaimer: 'Denne oppsummeringen er generert av AI og venter på redaksjonell godkjenning.',
		summaryApproved: 'Denne AI-genererte oppsummeringen er redaksjonelt godkjent.'
	},
	da: {
		nextPage: 'Næste side',
		nextPageHint: 'Klik på {button} eller brug tast {key} for at begynde at læse.',
		swipeRight: 'Swipe til højre for at se indholdsfortegnelsen.',
		swipeUp: 'Swipe op for at gå til næste artikel.',
		goBack: 'Gå tilbage',
		goForward: 'Gå videre',
		tableOfContents: 'Indholdsfortegnelse',
		nextArticle: 'Næste artikel',
		hideDone: 'Skjul læste',
		showDone: 'Vis læste',
		articlesMarkedAsRead: 'Artikler markeres som læst når du navigerer forbi sidste side',
		orUseShortcut: ', eller brug genvej',
		todaysEdition: 'Dagens udgave opsummeret',
		loadingSummary: 'Indlæser resumé...',
		summaryUnavailable: 'Resumé midlertidigt utilgængeligt',
		summaryDisclaimer: 'Denne opsummering er genereret af AI og afventer redaktionel godkendelse.',
		summaryApproved: 'Denne AI-genererede opsummering er blevet redaktionelt godkendt.'
	},
	de: {
		nextPage: 'Nächste Seite',
		nextPageHint: 'Klicken Sie auf {button} oder verwenden Sie Taste {key} um mit dem Lesen zu beginnen.',
		swipeRight: 'Wischen Sie nach rechts, um das Inhaltsverzeichnis zu sehen.',
		swipeUp: 'Wischen Sie nach oben, um zum nächsten Artikel zu gelangen.',
		goBack: 'Zurück',
		goForward: 'Vorwärts',
		tableOfContents: 'Inhaltsverzeichnis',
		nextArticle: 'Nächster Artikel',
		hideDone: 'Gelesene ausblenden',
		showDone: 'Gelesene anzeigen',
		articlesMarkedAsRead: 'Artikel werden als gelesen markiert, wenn Sie an der letzten Seite vorbei navigieren',
		orUseShortcut: ', oder verwenden Sie Tastenkürzel',
		todaysEdition: 'Heutige Ausgabe zusammengefasst',
		loadingSummary: 'Zusammenfassung wird geladen...',
		summaryUnavailable: 'Zusammenfassung vorübergehend nicht verfügbar',
		summaryDisclaimer: 'Diese Zusammenfassung wurde von KI erstellt und wartet auf redaktionelle Freigabe.',
		summaryApproved: 'Diese KI-generierte Zusammenfassung wurde redaktionell geprüft.'
	}
};

interface I18nConfig {
	language: Language;
	autoSummary: boolean;
}

/**
 * I18n wrapper class that encapsulates language and auto-summary configuration.
 * Initialized once at application startup with admin config.
 */
export class I18n {
	private language: Language;
	private autoSummary: boolean;

	constructor(config: I18nConfig = {language: 'en', autoSummary: true}) {
		this.language = config.language;
		this.autoSummary = config.autoSummary;
	}

	configure(config: Partial<I18nConfig>): void {
		if (config.language !== undefined) {
			this.language = config.language;
		}
		if (config.autoSummary !== undefined) {
			this.autoSummary = config.autoSummary;
		}
	}

	getLanguage(): Language {
		return this.language;
	}

	isAutoSummaryEnabled(): boolean {
		return this.autoSummary;
	}

	t(key: string): string {
		return UI_STRINGS[this.language]?.[key] || UI_STRINGS.en[key] || key;
	}

	tFormat(key: string, replacements: Record<string, string>): string {
		let text = this.t(key);
		for (const [placeholder, value] of Object.entries(replacements)) {
			text = text.replace(`{${placeholder}}`, value);
		}
		return text;
	}
}

// Singleton instance for application-wide use
const i18n = new I18n();

// Exported functions that delegate to singleton (maintains backward compatibility)
export function setLanguage(lang: Language): void {
	i18n.configure({language: lang});
}

export function getLanguage(): Language {
	return i18n.getLanguage();
}

export function setAutoSummary(enabled: boolean): void {
	i18n.configure({autoSummary: enabled});
}

export function isAutoSummaryEnabled(): boolean {
	return i18n.isAutoSummaryEnabled();
}

export function t(key: string): string {
	return i18n.t(key);
}

export function tFormat(key: string, replacements: Record<string, string>): string {
	return i18n.tFormat(key, replacements);
}
